/* eslint-disable complexity */
import CellTowerIcon from '@mui/icons-material/CellTower';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import {useEffect, useState} from 'react';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {CommunicationNode} from '../../interfaces/CommunicationNodeV2';
import {MapLatLangCoordinates} from '../common/Map';
import TruncatedText from '../common/TruncantedText';
import CommunicationNodeItemAck from './CommunicationNodeItemAck';
import CommunicationNodeItemUpdate from './CommunicationNodeItemUpdate';
import CommunicationNodeItemView from './CommunicationNodeItemView';

interface Props {
  pk: number;
  item?: CommunicationNode;
  mode?: 'view' | 'update' | 'ack' | 'update_from_info';
  prefetch?: boolean;
  locationCoordinates?: MapLatLangCoordinates;
  isActiveModal?: boolean;
  onClose?: () => void;
  onSubmitted?: () => void;
  onPurged?: () => void;
  onOpenHistory?: (externalId: number, type: 'cn' | 'commtracNodeByCn') => void;
}

const CommunicationNodeItem = ({
  pk,
  item,
  mode: initialMode = 'view',
  prefetch,
  locationCoordinates,
  isActiveModal,
  onClose,
  onSubmitted,
  onPurged,
  onOpenHistory,
}: Props) => {
  const [mode, setMode] = useState(initialMode);

  /*********/
  /* fetch */
  /*********/

  const [fetchedData, setFetchedData] = useState<CommunicationNode | undefined>(
    cloneDeep(item)
  );
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const fetchData = async () => {
    setFetchedInProgress(true);
    setFetchedErrors([]);
    try {
      const resp = await API.get<CommunicationNode>(
        `${apiBaseUrl}/node/${pk}/communication-node`
      );
      setFetchedData(resp.data);
      if (resp.data.ack !== '1') {
        setMode('ack');
      }
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_07a58967");
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  };

  useEffect(() => {
    if (prefetch) {
      fetchData();
    }
  }, [pk, prefetch]);

  useEffect(() => {
    if (item && !isEqual(item, fetchedData)) {
      setFetchedData(item);
    }
  }, [item]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      position="relative"
      gap={3}
      p={3}
    >
      <Box display="flex" justifyContent="space-between">
        <Box
          className="dragHandle"
          display="flex"
          alignItems="center"
          width="100%"
          gap={1.5}
          overflow="hidden"
          sx={{cursor: 'grab'}}
        >
          <CellTowerIcon sx={{color: 'primary.main'}} />
          {fetchedData ? (
            <TruncatedText fontSize={24}>{fetchedData.name} [CN]</TruncatedText>
          ) : null}
        </Box>
        <Box display="flex" gap={0.5}>
          <IconButton onClick={() => fetchData()}>
            <RefreshIcon />
          </IconButton>
          {onClose ? (
            <IconButton onClick={() => onClose()}>
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </Box>
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error">
          {error}{' '}
        </Alert>
      ))}
      {mode === 'view' ? (
        <CommunicationNodeItemView
          pk={pk}
          item={fetchedData}
          prefetch={!prefetch}
          onCancel={onClose}
          onEdit={() => setMode('update_from_info')}
          onAck={() => setMode('ack')}
          onPurged={onPurged}
          onFetched={setFetchedData}
          onOpenHistory={onOpenHistory}
          onSubmitted={onSubmitted}
        />
      ) : mode === 'update' || mode === 'update_from_info' ? (
        <CommunicationNodeItemUpdate
          mode={mode}
          pk={pk}
          item={fetchedData}
          prefetch={!prefetch}
          locationCoordinates={locationCoordinates}
          isActiveModal={isActiveModal}
          onCancel={() => {
            mode === 'update' ? onClose?.() : setMode('view');
          }}
          onSubmitted={() => {
            onSubmitted?.();
            mode === 'update' ? onClose?.() : setMode('view');
            fetchData();
          }}
        />
      ) : mode === 'ack' ? (
        <CommunicationNodeItemAck
          pk={pk}
          item={fetchedData}
          prefetch={!prefetch}
          locationCoordinates={locationCoordinates}
          isActiveModal={isActiveModal}
          onCancel={() => setMode('view')}
          onSubmitted={() => {
            onSubmitted?.();
            setMode('view');
            fetchData();
          }}
        />
      ) : null}
    </Box>
  );
};

export default CommunicationNodeItem;
